import styled from '@emotion/styled';
import _, { throttle } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import FormAddress from './oc-form-address';
import FormContact from './oc-form-contact';
import FormError from './oc-form-error';
import FormGroup from './oc-form-group';
import FormModal from './oc-form-modal';
import FormName from './oc-form-name';
import FormPage from './oc-form-page';
import FormProgress from './oc-form-progress';
import Theme from '../../../themes/index';
import Checkbox from '../input-elements/oc-checkbox';
import CheckboxGroup from '../input-elements/oc-checkbox-group';
import EmailInput from '../input-elements/oc-email-input';
import Fieldset from '../input-elements/oc-fieldset';
import FormButton from '../input-elements/oc-form-button';
import ImageRadioButton from '../input-elements/oc-image-radio-button';
import InputError from '../input-elements/oc-input-error';
import InputList from '../input-elements/oc-input-list';
import Label from '../input-elements/oc-label';
import NumberInput from '../input-elements/oc-numeric-input';
import PhoneInput from '../input-elements/oc-phone-input';
import RadioButton from '../input-elements/oc-radio-button';
import RadioGroup from '../input-elements/oc-radio-group';
import SelectDropdown from '../input-elements/oc-select-dropdown';
import TextInput from '../input-elements/oc-text-input';

// Form Component Map
const FieldMap = {
  text: TextInput,
  email: EmailInput,
  phone: PhoneInput,
  textarea: TextInput,
  number: NumberInput,
  checkbox: CheckboxGroup,
  radio: RadioGroup,
  imageRadio: RadioGroup,
  select: SelectDropdown,
};

// Styled Components
const Form = styled.form`
  background-color: #f8f8f8;
  display: inline-block;
  padding: 10px;
  position: relative;
  width: 100%;

  @media screen and (min-width: 768px) {
    padding: 40px;
    width: ${props => (props.showStartPage ? '62%' : '100%')};
  }
`;

const FormFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;

  .buttons-container {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 16px;
    position: relative;

    button {
      margin-right: 1em;
    }
  }

  .form-branding {
    align-items: center;
    display: flex;
    justify-content: flex-end;

    p {
      font-size: 12px;
      max-width: 120px;
      margin-right: 8px;
      overflow-wrap: break-word;
      text-align: right;
    }

    img {
      width: 33px;
    }
  }
`;

const Disclaimers = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
`;

const Note = styled.p`
  color: #585858;
  font-family: 'OCRoboto';
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
  margin-bottom: 8px;
`;

const FormWrapper = ({
  action,
  children,
  method = 'POST',
  pages,
  themeColor = Theme.colors.brand,
  showStartPage,
  successComponent,
  successComponentProps = {},
  uid,
  ...props
}) => {
  const formRef = useRef();
  const formProps = props;
  const pageCount = pages?.length;
  const [currentPage, setCurrentPage] = useState(0);
  const [isBlockedForSubmission, toggleBlockedForSubmission] = useState(false);
  const [pageData, setPageData] = useState(
    Array.from({ length: pageCount }, () => ({
      values: {},
      touched: {},
      errors: {},
    }))
  );
  const [formModalContent, setFormModalContent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const [submissionErrorChildren, setSubmissionErrorChildren] = useState(null);
  const [isPageInitialized, setPageInitialized] = useState(false);
  const lastPage = currentPage === pageCount - 1;
  const defaultNextText = lastPage ? 'Submit' : 'Next';
  const nextText = pages[currentPage]?.nextText || defaultNextText;
  const disclaimers = pages[currentPage]?.disclaimers || [];

  // TODO: QW-specific? Migrate form-specific values and functions
  // out of wrapper and pass into form via props
  const [estimateValues, setEstimateValues] = useState(null);
  const [formData, setFormData] = useState(null);

  const openFormModal = content => {
    const Component = content;
    setFormModalContent(<Component />);
  };

  const closeFormModal = () => {
    setFormModalContent(null);
  };

  // ------------------------------------------------------------------------------------------

  // Page value initialization
  const initializePageData = pages => {
    if (isPageInitialized) return;

    pages.forEach((page, pageIndex) => {
      const newPageData = { ...pageData[pageIndex] };
      page.fields
        .filter(field => field.value && !validationErrors(field, field.name, field.value.toString().trim()))
        .forEach(field => (newPageData.values[field.name] = field.value));
      if (Object.keys(newPageData.values).length > 0) {
        updatePageData(pageIndex, newPageData);
      }
    });

    setPageInitialized(true);
  };

  // Validation functions
  const updatePageData = (pageIndex, data) => {
    setPageData(existingData => [
      ...existingData.slice(0, pageIndex),
      (existingData[pageIndex] = data),
      ...existingData.slice(pageIndex + 1),
    ]);
  };

  const removeDependentValues = (fieldName, values) => {
    const conditionalFields = pages[currentPage].fields.filter(field => field.conditional);
    const dependentFields = conditionalFields.filter(field => field.conditional?.input == fieldName);
    const newValue = values[fieldName];

    // If a value with a conditional field dependency is removed,
    // also remove the dependent field's value.
    dependentFields.forEach(field => {
      if (field.conditional.anyValue) {
        if (!newValue?.length) delete values[field.name];
      } else if (Array.isArray(newValue)) {
        if (!newValue.includes(field.conditional?.value)) delete values[field.name];
      } else if (newValue != field.conditional?.value) delete values[field.name];
    });
  };

  const updateValues = (event, field, name = field.name, value = event.target.value, pageIndex = currentPage) => {
    const newPageData = { ...pageData[pageIndex] };
    const newValues = { ...pageData[pageIndex].values };
    const newValue = value;

    newValues[name] = newValue;
    removeDependentValues(name, newValues);

    newPageData.values = newValues;
    validateField(newPageData, field, name, newValue, pageIndex);
  };

  const updateTouched = (event, field) => {
    const newPageData = { ...pageData[currentPage] };
    const newTouched = { ...pageData[currentPage].touched };
    const key = field.name;
    const { value } = event.target;

    newTouched[key] = true;
    newPageData.touched = newTouched;
    validateField(newPageData, field, key, value);
  };

  const validEmail = value => /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i.test(value);

  const validPhone = value => /^(\([0-9]{3}\)) [0-9]{3}-[0-9]{4}$/.test(value);

  const conditionalFieldIsVisible = (pageIndex, field) => {
    const conditionedValue = pageData[pageIndex].values[field.conditional.input];

    // NOTE: So far we're specifically checking string and array values
    // for fields that are dependent on a specific selection
    if (field.conditional.anyValue) {
      return !!conditionedValue;
    }
    if (Array.isArray(conditionedValue)) {
      return conditionedValue.includes(field.conditional.value);
    }
    return field.conditional.value == conditionedValue;
  };

  const validateField = (newData, field, name, value, pageIndex = currentPage) => {
    const newPageData = newData;
    const newErrors = { ...pageData[pageIndex].errors };
    const formattedValue = value?.toString().trim();
    const errorMessage = validationErrors(field, name, formattedValue);

    if (field.conditional && !conditionalFieldIsVisible(pageIndex, field)) {
      delete newErrors[name];
    } else if (errorMessage) {
      newErrors[name] = errorMessage;
    } else {
      delete newErrors[name];
    }

    newPageData.errors = newErrors;
    updatePageData(pageIndex, newPageData);
  };

  const validationErrors = (field, name, value) => {
    const requiredError = field.requiredError || 'Please provide a value';

    if (field.required && !value) {
      return requiredError;
    }
    if ((name == 'email' || name == 'secondary-email') && value && !validEmail(value)) {
      return 'Please enter a valid email address';
    }
    if (name == 'phone' && value && !validPhone(value)) {
      return 'Please use the following format: (123) 456-7890';
    }
    return false;
  };

  // ------------------------------------------------------------------------------------------

  // Progression & submission functions
  const prevPage = event => {
    event.preventDefault();
    event.target.blur();

    if (pages[currentPage] && pages[currentPage].onPrevious) {
      pages[currentPage].onPrevious({ trigger: 'prevButton' });
    }

    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextOrSubmit = (event, args = {}) => {
    event.preventDefault();
    event.target.blur();

    let currentPageIsValid = false;
    const currentPageData = { ...pageData[currentPage] };
    const newErrors = { ...currentPageData.errors };

    pages[currentPage]?.fields.map(field => {
      if (field.conditional && !conditionalFieldIsVisible(currentPage, field)) {
        delete newErrors[field.name];
        return;
      }

      if (field.customValidation) {
        const validationObject = field.customValidation(currentPageData.values[field.name]);
        const { valid } = validationObject;
        const { error } = validationObject;

        if (valid) {
          delete newErrors[field.name];
        } else {
          newErrors[field.name] = error;
        }
      } else {
        const formattedValue = currentPageData.values[field.name]?.toString().trim();
        const errorMessage = validationErrors(field, field.name, formattedValue);

        if (errorMessage) {
          newErrors[field.name] = errorMessage;
        } else {
          delete newErrors[field.name];
        }
      }
    });

    currentPageData.errors = newErrors;
    updatePageData(currentPage, currentPageData);
    currentPageIsValid = _.isEmpty(currentPageData.errors);

    formRef.current.scrollIntoView();
    if (currentPageIsValid) {
      setSubmissionError(false);
      setSubmissionErrorChildren(null);

      if (pages[currentPage] && pages[currentPage].onNext) {
        const nextArgs = {
          pageData: pageData[currentPage]?.values,
          trigger: 'nextButton',
          ...args,
        };
        pages[currentPage].onNext(nextArgs);
      }

      lastPage ? handleSubmit() : setCurrentPage(currentPage + 1);
    } else {
      const error = 'There was an issue submitting your information. Please check all fields and resubmit.';
      _satellite.track('form-error', { errorMsg: error });
      setSubmissionError(error);
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    const formData = {};
    pages.forEach(page => {
      page.fields
        .filter(field => field.hidden)
        .forEach(field => {
          Object.assign(formData, { [field.name]: field.value });
        });
    });

    const pageValues = pageData.map(page => Object.assign(formData, page.values));

    const requestOptions = {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    };

    fetch(action, requestOptions)
      .then(response => {
        setIsSubmitting(false);

        if (response.status == 200) {
          setSubmissionError(null);
        } else {
          const error = 'There was an issue contacting the server; please try again.';
          _satellite.track('form-error', { errorMsg: error });
          setSubmissionError(error);
        }
        return response.json();
      })
      .then(data => {
        if (!data.error) {
          if (uid === 'quoting-widget') {
            data.result.estimateValues && setEstimateValues(data.result.estimateValues);
            data.result.form_submit && setFormData(data.result.form_submit.data_json);
          }
          setSubmissionSuccess(true);
        }
      })
      .catch(error => {
        _satellite.track('form-error', { errorMsg: error });
        setIsSubmitting(false);
        setSubmissionError(error);
      });
  };

  // Success messaging component
  if (submissionSuccess && successComponent) {
    return React.createElement(successComponent, {
      ...successComponentProps,
      formData,
      estimateValues,
    });
  }

  const renderField = (pageIndex, field, fieldIndex) => {
    const { values } = pageData[pageIndex];
    const { touched } = pageData[pageIndex];
    const { errors } = pageData[pageIndex];
    const Component = FieldMap[field.inputType];
    const helperText = field.helperText || (field.required ? 'Required' : null);
    const modalContent = field.infoModalContent;
    const valid = !errors.hasOwnProperty(field.name) && touched[field.name];
    const error = errors[field.name];

    if (['radio', 'imageRadio'].includes(field.inputType)) {
      const additionalField = field.additionalCheckbox;

      return (
        <Component
          key={`field-${fieldIndex}`}
          label={field.label}
          helperText={helperText}
          helperTextColor={field.helperTextColor}
          infoFunction={modalContent ? () => openFormModal(modalContent) : null}
          name={field.name}
          themeColor={themeColor}
          onChange={event => updateValues(event, field, field.name)}
          additionalChange={event => updateValues(event, additionalField, additionalField.name, event.target.checked)}
          options={field.options}
          imageRadio={field.inputType == 'imageRadio'}
          required={field.required}
          touched={touched[field.name] || (submissionError && !values[field.name])}
          valid={valid}
          error={error}
          {...field}
        />
      );
    }
    if (field.inputType == 'checkbox') {
      return (
        <Component
          key={`field-${fieldIndex}`}
          label={field.label}
          helperText={helperText}
          infoFunction={modalContent ? () => openFormModal(modalContent) : null}
          name={field.name}
          themeColor={themeColor}
          onChange={updateValues}
          options={field.options}
          required={field.required}
          touched={touched[field.name] || (submissionError && !values[field.name])}
          valid={valid}
          error={error}
          {...field}
        />
      );
    }
    if (field.inputType == 'select') {
      if (pageIsCurrent && !pageData[currentPage].values[field.name]) {
        const initialValue = typeof field.options[0] === 'string' ? field.options[0] : field.options[0].value;
        pageData[currentPage].values[field.name] = initialValue;
      }

      return (
        <Fieldset key={`field-${fieldIndex}`} margin="0 0 40px">
          <Component
            label={field.label}
            infoFunction={modalContent ? () => openFormModal(modalContent) : null}
            name={field.name}
            themeColor={themeColor}
            options={field.options}
            onChange={event => updateValues(event, field)}
            required={field.required}
            touched={touched[field.name] || (submissionError && !values[field.name])}
            valid={valid}
            error={error}
            {...field}
          />
        </Fieldset>
      );
    }
    return (
      <Fieldset key={`field-${fieldIndex}`} margin={field.hidden ? '0' : '0 0 32px'}>
        <Component
          label={field.label}
          helperText={helperText}
          helperTextColor={field.helperTextColor}
          infoFunction={modalContent ? () => openFormModal(modalContent) : null}
          name={field.name}
          themeColor={themeColor}
          multiline={field.inputType == 'textarea'}
          onBlur={event => updateTouched(event, field)}
          onChange={event => updateValues(event, field, field.name)}
          required={field.required}
          touched={touched[field.name] || (submissionError && !values[field.name])}
          valid={valid}
          error={error}
          defaultValue={field.defaultValue}
          {...field}
        />
      </Fieldset>
    );
  };

  initializePageData(pages);

  // Form components
  return (
    <>
      <Form action={action} id={uid} method={method} ref={formRef} showStartPage={showStartPage}>
        {pageCount > 1 && (
          <FormProgress
            pages={pages}
            currentPage={currentPage}
            marginBottom="32px"
            fullPage={uid === 'quoting-widget'}
          />
        )}

        {pages.map((page, pageIndex) => {
          const pageIsCurrent = pageIndex == currentPage;

          return (
            <FormPage
              active={pageIndex === currentPage}
              key={`page-${pageIndex}`}
              data={page.data}
              optionalStep={page.optionalStep}
              skipFunction={nextOrSubmit}
              hideBorder={page.hideBorder}
              title={page.title}
              titlePinkText={page.titlePinkText}
              description={page.description}
              cta={page.cta}
              openFormModal={openFormModal}
              error={submissionError}
              errorLabel={uid == 'quoting-widget' ? 'Error Submitting Form:' : null}
              errorChildren={submissionErrorChildren}
              themeColor={themeColor}
            >
              {!page.component &&
                page.fields?.map((field, fieldIndex) => {
                  if (field.conditional && !conditionalFieldIsVisible(pageIndex, field)) {
                    return;
                  }

                  return renderField(pageIndex, field, fieldIndex);
                })}

              {page.component &&
                React.createElement(page.component, {
                  ...page.componentProps,
                  blurValidation: updateTouched,
                  changeValidation: updateValues,
                  openFormModal,
                  closeFormModal,
                  fields: page.fields,
                  values: pageData[pageIndex].values,
                  touched: pageData[pageIndex].touched,
                  errors: pageData[pageIndex].errors,
                  submissionError,
                  setSubmissionError,
                  setSubmissionErrorChildren,
                  themeColor,
                  toggleBlockedForSubmission,
                  formProps,
                })}
            </FormPage>
          );
        })}

        <FormFooter>
          <div className="buttons-container">
            {currentPage > 0 && (
              <FormButton
                color="#FFFFFF"
                data-track="prev-step"
                disabled={isBlockedForSubmission}
                onClick={event => prevPage(event)}
              >
                Previous
              </FormButton>
            )}

            <FormButton
              color={themeColor}
              data-track={lastPage ? 'form-submit' : 'next-step'}
              disabled={isBlockedForSubmission}
              onClick={event => nextOrSubmit(event)}
            >
              {nextText}
            </FormButton>
          </div>
          {uid !== 'quoting-widget' && (
            <div className="form-branding">
              <p>Technology Powered by Owens Corning</p>
              <img
                src="https://imagecdn.owenscorning.com/ocimages/image/upload/v1575575892/meganav/oc-logo.svg"
                alt="Owens Corning Logo"
              />
            </div>
          )}

          {disclaimers && (
            <Disclaimers>
              {disclaimers.map(disclaimer => (
                <Note>{disclaimer}</Note>
              ))}
            </Disclaimers>
          )}
        </FormFooter>
      </Form>

      {formModalContent && (
        <FormModal
          data={{ 'data-am-region': 'Quoting Form Overlay' }}
          name="Quoting Form Modal"
          onClose={() => closeFormModal()}
          open
        >
          {formModalContent}
        </FormModal>
      )}
    </>
  );
};

export default FormWrapper;

FormWrapper.propTypes = {
  /* HTML: Data endpoint URL string for the form action attribute */
  action: PropTypes.string,
  /* HTML: Form submission method attribute – defaults to POST */
  method: PropTypes.string,
  /* JSX: Array of page data, including title, description, and fields */
  pages: PropTypes.arrayOf(PropTypes.object),
  /* HTML: String value to assign as form ID */
  uid: PropTypes.string,
  /* CSS: Hex value for color theme */
  themeColor: PropTypes.string,
};
